import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { useLocalizedPath } from '../i18n/localeRouting';

export default function AboutPage() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="min-h-screen bg-gradient-to-b from-page via-surface to-page py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-subtle bg-surface/70 backdrop-blur-theme px-4 py-2 text-sm text-secondary">
            <span className="font-semibold text-validator-text">{t('appName')}</span>
            <span className="text-muted">•</span>
            <span className="font-semibold">{t('taglineShort')}</span>
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl font-bold font-heading tracking-tight text-primary">
            {t('aboutTitle')}
          </h1>
          <p className="mt-4 text-lg text-secondary max-w-3xl mx-auto">
            {t('aboutSubtitle')}
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to={lp('/lookup')}
              className="inline-flex items-center justify-center rounded-btn px-6 py-3 font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-validator border-validator/40 bg-validator-subtle text-validator-text hover:bg-validator hover:text-white"
            >
              {t('aboutCtaValidate')}
            </Link>
            <Link
              to={lp('/word-finder')}
              className="inline-flex items-center justify-center rounded-btn px-6 py-3 font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-finder border-finder/30 bg-finder-subtle text-finder-text hover:bg-finder hover:text-white"
            >
              {t('aboutCtaFind')}
            </Link>
            <Link
              to={lp('/anagram-solver')}
              className="inline-flex items-center justify-center rounded-btn px-6 py-3 font-semibold border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-anagram border-anagram/30 bg-anagram-subtle text-anagram-text hover:bg-anagram hover:text-white"
            >
              {t('aboutCtaSolve')}
            </Link>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3 mb-10">
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 card-interactive transition-all">
            <div className="text-sm font-semibold text-validator-text">{t('aboutWhyTitle')}</div>
            <h2 className="mt-2 text-xl font-bold text-primary">{t('aboutWhyHeadline')}</h2>
            <p className="mt-3 text-secondary">{t('aboutWhyBody')}</p>
          </div>

          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 card-interactive transition-all">
            <div className="text-sm font-semibold text-finder-text">{t('aboutHowTitle')}</div>
            <h2 className="mt-2 text-xl font-bold text-primary">{t('aboutHowHeadline')}</h2>
            <ul className="mt-3 space-y-2 text-secondary">
              <li>{t('aboutHowBullet1')}</li>
              <li>{t('aboutHowBullet2')}</li>
              <li>{t('aboutHowBullet3')}</li>
            </ul>
          </div>

          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 card-interactive transition-all">
            <div className="text-sm font-semibold text-anagram-text">{t('aboutBuiltTitle')}</div>
            <h2 className="mt-2 text-xl font-bold text-primary">{t('aboutBuiltHeadline')}</h2>
            <p className="mt-3 text-secondary">{t('aboutBuiltBody')}</p>
          </div>
        </div>

        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-primary">{t('aboutToolsTitle')}</h2>
              <p className="mt-2 text-secondary max-w-3xl">{t('aboutToolsSubtitle')}</p>
            </div>
            <Link
              to={lp('/')}
              className="inline-flex items-center justify-center rounded-btn px-5 py-3 font-semibold border border-subtle bg-surface text-primary hover:bg-hover transition-colors"
            >
              {t('aboutCtaBackHome')}
            </Link>
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-3">
            <div className="rounded-card border border-validator/20 bg-validator-subtle/60 p-5">
              <div className="text-sm font-semibold text-validator-text">{t('navValidator')}</div>
              <p className="mt-2 text-secondary">{t('aboutToolValidatorDesc')}</p>
            </div>
            <div className="rounded-card border border-finder/20 bg-finder-subtle/60 p-5">
              <div className="text-sm font-semibold text-finder-text">{t('navFinder')}</div>
              <p className="mt-2 text-secondary">{t('aboutToolFinderDesc')}</p>
            </div>
            <div className="rounded-card border border-anagram/20 bg-anagram-subtle/60 p-5">
              <div className="text-sm font-semibold text-anagram-text">{t('navAnagram')}</div>
              <p className="mt-2 text-secondary">{t('aboutToolAnagramDesc')}</p>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
            <h2 className="text-2xl font-bold text-primary">{t('aboutWhatYouGetTitle')}</h2>
            <ul className="mt-4 space-y-2 text-secondary">
              <li>{t('aboutWhatYouGet1')}</li>
              <li>{t('aboutWhatYouGet2')}</li>
              <li>{t('aboutWhatYouGet3')}</li>
              <li>{t('aboutWhatYouGet4')}</li>
            </ul>
          </div>

          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-8 card-interactive transition-all">
            <h2 className="text-2xl font-bold text-primary">{t('aboutPlayfulTitle')}</h2>
            <p className="mt-4 text-secondary">{t('aboutPlayfulBody')}</p>
            <div className="mt-5 rounded-btn border border-subtle bg-surface-alt p-4 text-sm text-secondary">
              {t('aboutPlayfulTip')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
